/**
 * Helper utilities for Fehlzeiten (Absence) service layer.
 *
 * Contains date formatting, data mapping, and transformation functions.
 *
 * @module fehlzeiten/services/helpers
 */

import type { FehlzeitEntry, LatenessEntry } from "../types";

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Converts a German date format (DD.MM.YYYY) to ISO format (YYYY-MM-DD).
 *
 * @param date - Date string in DD.MM.YYYY or YYYY-MM-DD format
 * @returns ISO formatted date string (YYYY-MM-DD)
 *
 * @example
 * toIsoDate('15.01.2026') // Returns '2026-01-15'
 * toIsoDate('2026-01-15') // Returns '2026-01-15' (unchanged)
 */
export function toIsoDate(date: string): string {
  if (!date) return date;
  // Already ISO format
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  // Convert DD.MM.YYYY to YYYY-MM-DD
  const parts = date.split(".");
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return date;
}

/**
 * Converts an ISO date (YYYY-MM-DD) to German format (DD.MM.YYYY).
 *
 * @param date - ISO date string
 * @returns German formatted date string
 *
 * @example
 * toGermanDate('2026-01-15') // Returns '15.01.2026'
 */
export function toGermanDate(date: string): string {
  if (!date) return "";
  try {
    return new Date(date).toLocaleDateString("de-DE");
  } catch {
    return date;
  }
}

/**
 * Formats a timestamp to German date-time format.
 *
 * @param timestamp - ISO timestamp or Date object
 * @returns Formatted string like "15.01.2026, 14:30"
 */
export function formatDateTime(
  timestamp: string | Date | null | undefined,
): string {
  if (!timestamp) return "";
  try {
    const date =
      typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

/**
 * Parses a time range string into start and end times.
 *
 * @param dateRange - Time range like "10:15–11:45"
 * @returns Object with startTime and endTime, or nulls if parsing fails
 *
 * @example
 * parseTimeRange('10:15–11:45') // Returns { startTime: '10:15', endTime: '11:45' }
 */
export function parseTimeRange(dateRange: string | undefined): {
  startTime: string | null;
  endTime: string | null;
} {
  if (!dateRange) return { startTime: null, endTime: null };
  const parts = String(dateRange)
    .split("–")
    .map((s) => s.trim());
  return {
    startTime: parts[0] || null,
    endTime: parts[1] || null,
  };
}

// ============================================================================
// Data Mapping
// ============================================================================

/**
 * Maps raw RPC response row to FehlzeitEntry format.
 *
 * @param row - Raw row from fn_get_fehlzeiten_tab_data_v1 RPC
 * @returns Formatted FehlzeitEntry object
 */
export function mapRpcRowToFehlzeitEntry(
  row: Record<string, unknown>,
): FehlzeitEntry {
  // start_date from RPC is the ORIGINAL start date of the absence note (for multi-day)
  // record_date is the expanded day (each day within the range)
  const originalStartDate = row.start_date as string | undefined;
  const recordDate = row.record_date as string | undefined;
  const endDate = row.end_date as string | undefined;

  return {
    id: row.id as string,
    studentId: row.student_id as string,
    classId: row.class_id as string,
    studentName: (row.student_name as string) || "",
    studentClass: (row.class_name as string) || "",
    courseClass: (row.class_name as string) || "",
    date: recordDate ? toGermanDate(recordDate) : "",
    recordDateIso: recordDate,
    dateRange: row.date_range as string | undefined,
    // For multi-day: startDate should be the ORIGINAL start, not the expanded day
    startDate: originalStartDate
      ? toGermanDate(originalStartDate)
      : recordDate
      ? toGermanDate(recordDate)
      : undefined,
    // Also store the ISO version for the edit form
    start_date: originalStartDate || recordDate,
    endDate: row.end_date ? toGermanDate(row.end_date as string) : undefined,
    end_date: row.end_date as string | undefined,
    isMultiDay: (row.is_multi_day as boolean) || false,
    is_multi_day: (row.is_multi_day as boolean) || false,
    duration: (row.duration as FehlzeitEntry["duration"]) || "",
    // Explicit time bounds (Mai 2026): exposed by the RPC so partial-day
    // rows (e.g. orphan 08:00-11:20) can scope SAN writes to their slice
    // instead of getting a ganztägig note that clobbers the rest of the day.
    start_time: row.start_time as string | undefined,
    end_time: row.end_time as string | undefined,
    status: (row.status as FehlzeitEntry["status"]) || "ungeklärt",
    isExcusedLateness: row.is_excused_late as boolean | undefined,
    reason: (row.reason as string) || "",
    hasAttachment: (row.has_attachment as boolean) || false,
    hasAccess: true,
    createdBy: (row.created_by as string) || "",
    createdByName: (row.created_by_name as string) || "",
    createdAt: formatDateTime(row.created_at as string),
    sourceType:
      (row.source_type as FehlzeitEntry["sourceType"]) || "schuleingabe",
    submittedVia: row.submitted_via as FehlzeitEntry["submittedVia"],
    absenceNoteId: row.absence_note_id as string | undefined,
    approvalStatus: row.approval_status as string | undefined,
    approvalAction: row.approval_action as string | undefined,
    approvedByName: (row.approved_by_name as string) || "",
    approvedAt: row.approved_at
      ? formatDateTime(row.approved_at as string)
      : "",
    latenessCount: (row.lateness_count as number) || 0,
    latenessEntries:
      (row.lateness_entries as LatenessEntry[] | null) ?? undefined,
    excludeFromZeugnis: (row.exclude_from_zeugnis as boolean) || false,
    excludeFromZeugnisReason: (row.exclude_from_zeugnis_reason as string) || "",
    // Per-lesson lateness fields
    lessonId: row.lesson_id as string | undefined,
    lessonInfo: row.lesson_info as string | undefined,
    lateMinutes: (row.late_minutes as number) || 0,
    // Consecutive absence streak dates (from attendance logs)
    streakStartDate: row.streak_start_date as string | undefined,
    streakEndDate: row.streak_end_date as string | undefined,
  };
}

/**
 * Maps Klassenuebersicht RPC row to typed object.
 *
 * Returns snake_case property names to match the RPC output and
 * the consuming code's expectations.
 *
 * @param row - Raw row from fn_get_klassenuebersicht_complete RPC
 * @returns Typed row with snake_case properties
 */
export function mapKlassenuebersichtRow(row: Record<string, unknown>): {
  student_id: string;
  student_name: string;
  total_absence_days: number;
  excused_absence_days: number;
  unexcused_absence_days: number;
  /** Mai 2026: pure-absent_unclear days. Rendered as orange badge in
   *  Fehltage (U) cell alongside unexcused_absence_days (red). */
  ungeklaert_absence_days: number;
  total_fehlstunden: number;
  excused_fehlstunden: number;
  unexcused_fehlstunden: number;
  /** Mai 2026: absent_unclear lessons. Orange badge in Fehlstunden (U). */
  ungeklaert_fehlstunden: number;
  total_lateness_count: number;
  excused_lateness_count: number;
  unexcused_lateness_count: number;
  excused_lateness_minutes: number;
  unexcused_lateness_minutes: number;
  ungeklaert_count: number;
} {
  return {
    student_id: row.student_id as string,
    student_name: row.student_name as string,
    total_absence_days: Number(row.total_absence_days) || 0,
    excused_absence_days: Number(row.excused_absence_days) || 0,
    unexcused_absence_days: Number(row.unexcused_absence_days) || 0,
    ungeklaert_absence_days: Number(row.ungeklaert_absence_days) || 0,
    total_fehlstunden: Number(row.total_fehlstunden) || 0,
    excused_fehlstunden: Number(row.excused_fehlstunden) || 0,
    unexcused_fehlstunden: Number(row.unexcused_fehlstunden) || 0,
    ungeklaert_fehlstunden: Number(row.ungeklaert_fehlstunden) || 0,
    total_lateness_count: Number(row.total_lateness_count) || 0,
    excused_lateness_count: Number(row.excused_lateness_count) || 0,
    unexcused_lateness_count: Number(row.unexcused_lateness_count) || 0,
    excused_lateness_minutes: Number(row.excused_lateness_minutes) || 0,
    unexcused_lateness_minutes: Number(row.unexcused_lateness_minutes) || 0,
    ungeklaert_count: Number(row.ungeklaert_count) || 0,
  };
}

/**
 * Maps student absence detail row from RPC.
 *
 * @param row - Raw row from fn_get_student_absence_details RPC
 * @returns Typed StudentAbsenceDetail
 */
export function mapStudentAbsenceDetailRow(row: Record<string, unknown>): {
  id: string;
  dateIso: string;
  dateDisplay: string;
  timeRange: string;
  subjectName: string | null;
  periodLabel: string | null;
  status: string;
  isExcused: boolean;
  minutes: number;
  reason: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  lessonId: string | null;
  classAtLessonName: string | null;
} {
  return {
    id: row.id as string,
    dateIso: row.date_iso as string,
    dateDisplay: row.date_display as string,
    timeRange: row.time_range as string,
    subjectName: row.subject_name as string | null,
    periodLabel: row.period_label as string | null,
    status: row.status as string,
    isExcused: row.is_excused as boolean,
    minutes: Number(row.minutes) || 0,
    reason: row.reason as string | null,
    reviewedByName: row.reviewed_by_name as string | null,
    reviewedAt: row.reviewed_at as string | null,
    lessonId: row.lesson_id as string | null,
    classAtLessonName: (row.class_at_lesson_name as string | null) ?? null,
  };
}

// ============================================================================
// Validation
// ============================================================================

/**
 * Validates that a string is a valid UUID.
 *
 * @param value - String to validate
 * @returns True if valid UUID
 */
export function isValidUuid(value: string | undefined | null): boolean {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  );
}

/**
 * Validates that a string is a valid ISO date.
 *
 * @param value - String to validate
 * @returns True if valid ISO date (YYYY-MM-DD)
 */
export function isValidIsoDate(value: string | undefined | null): boolean {
  if (!value) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value);
  return !isNaN(date.getTime());
}

/**
 * Normalizes a time string to HH:MM format.
 *
 * @param time - Time string like "9:30" or "09:30"
 * @returns Normalized time "09:30" or null if invalid
 */
export function normalizeTime(time: string | null | undefined): string | null {
  if (!time) return null;
  const trimmed = time.trim();
  if (!trimmed) return null;
  // Match HH:MM or H:MM, optionally with :SS
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const hours = match[1].padStart(2, "0");
  const minutes = match[2];
  const seconds = match[3];
  return seconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
}
